import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from "react-router-dom";
import toast from '../../service/toaster';
import profileService from '../../service/profileService';
import blogService from '../../service/blogService';
import matchService from '../../service/matchService';
import './profile.css';

const getInitials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return 'CP';
};

const stripHtml = (html = '') => html.replace(/<[^>]+>/g, '');

export default function Profile() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [profileData, setProfileData] = useState(null);
  const [userBlogs, setUserBlogs] = useState([]);
  const [liveMatches, setLiveMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formState, setFormState] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    location: '',
    bio: '',
    about: '',
    role: '',
    profileImage: '',
  });

  const localUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('userDetails') || '{}');
    } catch {
      return {};
    }
  }, []);

  const fullName = useMemo(
    () =>
      profileData?.firstName || profileData?.lastName
        ? `${profileData?.firstName || ''} ${profileData?.lastName || ''}`.trim()
        : localUser?.name || localUser?.fullName || localUser?.username || localUser?.email || 'User',
    [localUser, profileData]
  );

  const avatarImg =
    profileData?.profileImage ||
    localUser?.profileImage;

  const avatarSrc = avatarImg

  useEffect(() => {
    const load = async () => {
      try {
        const [{ data: profile }, blogsResp, matchesResp] = await Promise.all([
          profileService.getProfile().catch(() => ({ data: null })),
          blogService.getBlog(1, 4).catch(() => ({ data: [] })),
          matchService.getMatches(1, 4, 'Live').catch(() => ({ data: [] })),
        ]);
        setProfileData(profile);
        const userId = localUser?._id;
        const filteredBlogs = (blogsResp?.data || []).filter((b) => {
          if (userId && b.authorId) return b.authorId === userId;
          if (b.author && fullName) return b.author.toLowerCase().includes(fullName.toLowerCase());
          return false;
        });
        setUserBlogs(filteredBlogs);
        setLiveMatches(matchesResp?.data || []);
      } catch (err) {
        toast.error('Unable to load profile');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [fullName, localUser?._id]);

  if (loading) {
    return (
      <div className="profile-loader">
        <div className="spinner" />
        <p>Loading profile…</p>
      </div>
    );
  }

  const openEditor = () => {
    setFormState({
      firstName: profileData?.firstName || '',
      lastName: profileData?.lastName || '',
      email: profileData?.email || localUser?.email || '',
      mobile: profileData?.mobile || '',
      location: profileData?.location || '',
      bio: profileData?.bio || '',
      about: profileData?.about || '',
      role: profileData?.role || '',
      profileImage: avatarImg || '',
    });
    setEditing(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      const payload = { ...formState };
      const resp = await profileService.updateProfile(payload);
      const updatedProfile = resp?.data || payload;
      setProfileData((prev) => ({ ...prev, ...updatedProfile }));
      const localDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
      const merged = {
        ...localDetails,
        firstName: payload.firstName || localDetails.firstName,
        lastName: payload.lastName || localDetails.lastName,
        name: `${payload.firstName || ''} ${payload.lastName || ''}`.trim() || localDetails.name,
        profileImage: updatedProfile.profileImage || payload.profileImage || localDetails.profileImage,
      };
      localStorage.setItem('userDetails', JSON.stringify(merged));
      toast.success('Profile updated');
      setEditing(false);
    } catch (err) {
      toast.error('Could not update profile');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    (async () => {
      try {
        setUploading(true);
        const res = await profileService.uploadProfileImage(file);
        const uploaded = res?.url
        if (uploaded) {
          setFormState((prev) => ({ ...prev, profileImage: uploaded }));
          toast.success('Image uploaded');
        } else {
          toast.error('Upload succeeded but no image URL returned');
        }
      } catch (err) {
        toast.error('Upload failed');
      } finally {
        setUploading(false);
      }
    })();
  };

  return (
    <div className="profile-frame">
      <div className="hero">
        <div className="hero__bg" />
        <div className="hero__content">
          <div className="hero__card">
            <div className="hero__identity">
              <div className="hero__avatar">
                <img src={avatarSrc} alt={fullName} />
              </div>
              <div>
                <div className="hero__name-row">
                  <h1>{fullName}</h1>
                  <span className="chip">Verified</span>
                </div>
                <p className="hero__subtitle">
                  {profileData?.bio || 'Sharing cricket reads, predictions, and live match takes.'}
                </p>
                <div className="hero__pills">
                  {profileData?.email && <span className="pill">{profileData.email}</span>}
                  {profileData?.mobile && <span className="pill">{profileData.mobile}</span>}
                  {profileData?.location && <span className="pill">{profileData.location}</span>}
                </div>
              </div>
            </div>
            <div className="hero__actions">
              <button className="ghost">Share</button>
              <button className="primary" onClick={openEditor}>Edit Profile</button>
            </div>
          </div>
        </div>
        <div className="hero__stats">
          <div>
            <p className="label">Blogs</p>
            <strong>{userBlogs.length}</strong>
          </div>
          <div>
            <p className="label">Live Matches</p>
            <strong>{liveMatches.length}</strong>
          </div>
          <div>
            <p className="label">Followers</p>
            <strong>{profileData?.followers || 0}</strong>
          </div>
          <div>
            <p className="label">Following</p>
            <strong>{profileData?.following || 0}</strong>
          </div>
        </div>
      </div>

      <div className="profile-grid">
        <section className="panel span-2">
          <div className="panel__head">
            <h3>About</h3>
            <span className="chip ghost">Profile</span>
          </div>
          <p className="body">{profileData?.about || 'Passionate about cricket tactics, live odds, and community predictions.'}</p>
          <div className="about-cards">
            <div>
              <p className="label">Role</p>
              <strong>{profileData?.role || 'Analyst'}</strong>
            </div>
            <div>
              <p className="label">Location</p>
              <strong>{profileData?.location || 'Worldwide'}</strong>
            </div>
            <div>
              <p className="label">Member since</p>
              <strong>{profileData?.createdAt ? new Date(profileData.createdAt).toLocaleDateString() : 'N/A'}</strong>
            </div>
            <div>
              <p className="label">Contact</p>
              <strong>{profileData?.mobile || '—'}</strong>
            </div>
          </div>
        </section>

        <section className="panel">
          <div className="panel__head">
            <h3>Floating Matches</h3>
            <button className="ghost" onClick={() => navigate('/match')}>View all</button>
          </div>
          {liveMatches.length === 0 ? (
            <div className="empty">No live matches right now.</div>
          ) : (
            <ul className="match-list">
              {liveMatches.map((match) => (
                <li key={match._id}>
                  <div>
                    <div className="match-title">{match.title}</div>
                    <div className="match-meta">
                      <span className="pill ghost">{match.matchStatus}</span>
                      {match.published_at && <span className="pill ghost">{match.published_at}</span>}
                    </div>
                  </div>
                  <button className="primary" onClick={() => navigate(`/prediction/add/${match._id}`)}>Predict</button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="panel span-2">
          <div className="panel__head">
            <h3>Your Blogs</h3>
            <button className="primary ghost" onClick={() => navigate('/blogs/add')}>New Blog</button>
          </div>
          {userBlogs.length === 0 ? (
            <div className="empty">No blogs yet. Start sharing your takes.</div>
          ) : (
            <div className="blog-tiles">
              {userBlogs.map((blog) => (
                <article
                  key={blog._id}
                  className="blog-tile"
                  onClick={() => navigate(`/blogs/view/${blog._id}`)}
                  style={{
                    backgroundImage: blog.images ? `linear-gradient(180deg, rgba(0,0,0,0.5), rgba(0,0,0,0.85)), url(data:image/jpeg;base64,${blog.images})` : undefined,
                  }}
                >
                  <div className="blog-tile__content">
                    <h4>{blog.title}</h4>
                    <p>{stripHtml(blog.content || '').slice(0, 140)}...</p>
                    <div className="blog-tile__meta">
                      <span>{blog.published_at}</span>
                      <span>{blog.tags?.[0]?.split(',').length || 0} tags</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>

      {editing && (
        <div className="profile-modal-overlay">
          <div className="profile-modal">
            <div className="modal-head">
              <h3>Edit Profile</h3>
              <button className="ghost" onClick={() => setEditing(false)}>Close</button>
            </div>
            <div className="modal-body">
              <div className="modal-grid">
                <label>
                  <span>First Name</span>
                  <input name="firstName" value={formState.firstName} onChange={handleChange} />
                </label>
                <label>
                  <span>Last Name</span>
                  <input name="lastName" value={formState.lastName} onChange={handleChange} />
                </label>
                <label>
                  <span>Email</span>
                  <input name="email" value={formState.email} disabled />
                </label>
                <label>
                  <span>Mobile</span>
                  <input name="mobile" value={formState.mobile} onChange={handleChange} />
                </label>
                <label>
                  <span>Location</span>
                  <input name="location" value={formState.location} onChange={handleChange} />
                </label>
                <label>
                  <span>Role</span>
                  <input name="role" value={formState.role} onChange={handleChange} />
                </label>
              </div>
              <label className="full">
                <span>Bio</span>
                <textarea name="bio" rows="2" value={formState.bio} onChange={handleChange} />
              </label>
              <label className="full">
                <span>About</span>
                <textarea name="about" rows="3" value={formState.about} onChange={handleChange} />
              </label>
              <label className="full">
                <span>Profile Image URL / Base64</span>
                <input name="profileImage" value={formState.profileImage} onChange={handleChange} />
              </label>
              <div className="upload-row">
                <button className="ghost" type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                  {uploading ? 'Uploading…' : 'Upload Image'}
                </button>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                />
              </div>
              {formState.profileImage && (
                <div className="preview">
                  <img src={formState?.profileImage} alt="preview" />
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button className="ghost" onClick={() => setEditing(false)}>Cancel</button>
              <button className="primary" onClick={handleSave}>Save changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
